import { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AppShell, RequireAuth, RequireRole, useAuth, useBrand } from '@aralere/shared-ui';
import { ClientDashboard } from './routes/client/Dashboard';
import { ClientServices } from './routes/client/Services';
import { AccountSettings } from './routes/client/AccountSettings';
import { ClientDocuments } from './routes/client/Documents';
import { ClientBilling } from './routes/client/Billing';
import { AdminDashboard } from './routes/admin/Dashboard';
import { AdminClients } from './routes/admin/Clients';
import { AdminInvite } from './routes/admin/Invite';
import { AdminServices } from './routes/admin/Services';
import { AdminPackages } from './routes/admin/Packages';
import { AdminVendors } from './routes/admin/Vendors';
import { AdminExpenses } from './routes/admin/Expenses';
import { AdminTimeEntries } from './routes/admin/TimeEntries';
import { AdminMileageEntries } from './routes/admin/MileageEntries';
import { AdminProjects } from './routes/admin/Projects';
import { AdminDocuments } from './routes/admin/Documents';
import { AdminInvoices } from './routes/admin/Invoices';
import { AdminPayments } from './routes/admin/Payments';
import { AdminArAging } from './routes/admin/ArAging';
import { AdminRemittances } from './routes/admin/Remittances';
import { AdminTaxReview } from './routes/admin/TaxReview';
import { AdminNotifications } from './routes/admin/Notifications';
import { AdminSettings } from './routes/admin/Settings';
import { Landing } from './routes/Landing';
import { Logout, SignedOut } from './routes/Logout';
import { adminNavItems } from './adminSections';

export function App(): JSX.Element {
  const { user, adminSurface, collectsForOthers, loading, activeClient } = useAuth();
  const { brand } = useBrand();

  // Glass intensity render binding (spec §6.2). The per-client override wins; otherwise
  // the brand's default; otherwise "default". Re-runs as /api/brand and /api/me resolve,
  // and when a client signs out (activeClient → null) it falls back to the brand default.
  const glassIntensity = activeClient?.glassIntensityOverride ?? brand?.branding.glassIntensityDefault ?? 'default';
  useEffect(() => {
    document.documentElement.setAttribute('data-glass', glassIntensity);
  }, [glassIntensity]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-sm text-[var(--color-muted)]">Loading…</div>
      </div>
    );
  }

  // 08 Q10: the admin experience (nav set + the `/` landing redirect) keys off
  // adminSurface, not the raw role — so a platform-admin who signs in on a
  // non-admin host (ADMIN_HOSTNAME) transparently gets the CLIENT view (client
  // nav + dashboard) rather than being bounced to /admin and stranded on a
  // "Not authorized" page. The admin ROUTES below still guard on the role via
  // RequireRole (which itself also checks adminSurface); the server is the gate.
  const showAdmin = adminSurface;

  // §7.10 non-payment lockout: a billing-suspended client is confined to the payment page until they
  // pay (which auto-reactivates). Admins are never locked. The server remains the authority — this is
  // a UX gate that steers a suspended client straight to Billing.
  const locked = !showAdmin && !!activeClient?.billingSuspended;
  const gated = (el: JSX.Element): JSX.Element => (locked ? <Navigate to="/billing" replace /> : el);

  // §7.10 slice 8c partial tier: a 'past-due' client (SOME services non-payment-suspended, not all)
  // is NOT fully locked — they may open any non-suspended service — but their default landing is
  // steered to /billing. So the '/' route redirects for BOTH tiers; the other client routes stay open
  // in the past-due tier (only `locked` gates those).
  const pastDue = !showAdmin && !!activeClient?.billingPastDue;
  const landingGated = (el: JSX.Element): JSX.Element => (locked || pastDue ? <Navigate to="/billing" replace /> : el);

  // Admin nav derives from the single ADMIN_SECTIONS source (adminSections.ts) so the top nav
  // and the dashboard cards can't drift. Client nav stays inline. When there is NO signed-in user
  // (Landing / signed-out), show NO nav items — the header links shouldn't appear before sign-in.
  const navItems = !user
    ? []
    : showAdmin
      ? adminNavItems({ collectsForOthers })
      : locked
        ? [{ label: 'Billing', to: '/billing' }]
        : [
            { label: 'Dashboard', to: '/' },
            { label: 'Services', to: '/services' },
            { label: 'Billing', to: '/billing' },
            { label: 'Documents', to: '/documents' },
            { label: 'Account', to: '/account' },
          ];

  return (
    <AppShell navItems={navItems}>
      <Routes>
        <Route path="/" element={user ? (showAdmin ? <Navigate to="/admin" /> : landingGated(<ClientDashboard />)) : <Landing />} />
        <Route
          path="/services"
          element={
            <RequireAuth>
              {gated(<ClientServices />)}
            </RequireAuth>
          }
        />
        <Route path="/logout" element={<Logout />} />
        <Route path="/signed-out" element={<SignedOut />} />
        <Route
          path="/billing"
          element={
            <RequireAuth>
              <ClientBilling />
            </RequireAuth>
          }
        />
        <Route
          path="/documents"
          element={
            <RequireAuth>
              {gated(<ClientDocuments />)}
            </RequireAuth>
          }
        />
        <Route
          path="/account"
          element={
            <RequireAuth>
              {gated(<AccountSettings />)}
            </RequireAuth>
          }
        />
        <Route
          path="/admin"
          element={
            <RequireAuth>
              <RequireRole role="platform-admin">
                <AdminDashboard />
              </RequireRole>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/clients"
          element={
            <RequireAuth>
              <RequireRole role="platform-admin">
                <AdminClients />
              </RequireRole>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/clients/invite"
          element={
            <RequireAuth>
              <RequireRole role="platform-admin">
                <AdminInvite />
              </RequireRole>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/services"
          element={
            <RequireAuth>
              <RequireRole role="platform-admin">
                <AdminServices />
              </RequireRole>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/packages"
          element={
            <RequireAuth>
              <RequireRole role="platform-admin">
                <AdminPackages />
              </RequireRole>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/vendors"
          element={
            <RequireAuth>
              <RequireRole role="platform-admin">
                <AdminVendors />
              </RequireRole>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/expenses"
          element={
            <RequireAuth>
              <RequireRole role="platform-admin">
                <AdminExpenses />
              </RequireRole>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/time"
          element={
            <RequireAuth>
              <RequireRole role="platform-admin">
                <AdminTimeEntries />
              </RequireRole>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/projects"
          element={
            <RequireAuth>
              <RequireRole role="platform-admin">
                <AdminProjects />
              </RequireRole>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/documents"
          element={
            <RequireAuth>
              <RequireRole role="platform-admin">
                <AdminDocuments />
              </RequireRole>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/mileage"
          element={
            <RequireAuth>
              <RequireRole role="platform-admin">
                <AdminMileageEntries />
              </RequireRole>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/invoices"
          element={
            <RequireAuth>
              <RequireRole role="platform-admin">
                <AdminInvoices />
              </RequireRole>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/payments"
          element={
            <RequireAuth>
              <RequireRole role="platform-admin">
                <AdminPayments />
              </RequireRole>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/ar-aging"
          element={
            <RequireAuth>
              <RequireRole role="platform-admin">
                <AdminArAging />
              </RequireRole>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/remittances"
          element={
            <RequireAuth>
              <RequireRole role="platform-admin">
                <AdminRemittances />
              </RequireRole>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/tax-review"
          element={
            <RequireAuth>
              <RequireRole role="platform-admin">
                <AdminTaxReview />
              </RequireRole>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/notifications"
          element={
            <RequireAuth>
              <RequireRole role="platform-admin">
                <AdminNotifications />
              </RequireRole>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/settings"
          element={
            <RequireAuth>
              <RequireRole role="platform-admin">
                <AdminSettings />
              </RequireRole>
            </RequireAuth>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AppShell>
  );
}
